// Coalesces + TTL-caches a Better Auth JWT for the console bearer (admin/api.ts) and
// the cockpit WS token (lib/ws-url.ts). Minting per request rate-limits (429) and
// leaves callers tokenless → 401/1008.

export interface TokenCache {
  get(): Promise<string | null>;
  invalidate(): void;
}

// Callers reconnect fastest exactly when auth is broken, so an uncooled retry hits the
// endpoint hardest at its worst moment.
const FAILURE_COOLDOWN_MS = 10_000;

// `ttlMs` should sit well inside the token's signing lifetime (the BFF signs 15m JWTs).
export function createTokenCache(
  mint: () => Promise<string | null>,
  ttlMs: number,
  now: () => number = Date.now,
): TokenCache {
  let cached: { value: string; expiresAt: number } | null = null;
  let inFlight: Promise<string | null> | null = null;
  let failedUntil = 0;
  // A mint racing an invalidate() must not write its result — it would resurrect the
  // very token that was just refused.
  let generation = 0;

  return {
    async get(): Promise<string | null> {
      if (cached && now() < cached.expiresAt) return cached.value;
      if (now() < failedUntil) return null;
      if (!inFlight) {
        const gen = generation;
        inFlight = mint()
          .then((token) => {
            if (token) {
              if (gen === generation) cached = { value: token, expiresAt: now() + ttlMs };
            } else {
              failedUntil = now() + FAILURE_COOLDOWN_MS;
            }
            return token;
          })
          .finally(() => {
            // `finally`, not `then`: a rejected mint would otherwise poison every get().
            inFlight = null;
          });
      }
      return inFlight;
    },
    invalidate(): void {
      cached = null;
      generation += 1;
    },
  };
}
